import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag, Lock, Tag, Check, Truck } from "lucide-react";
import { toast } from "sonner";
import WhatsAppIcon from "./WhatsAppIcon";
import { buildCartMessage, whatsappLink } from "../whatsapp";
import { COUPONS } from "../data";
import { saveOrder } from "../firebase";

export default function CartDrawer({ open, onClose, cart }) {
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const applyCoupon = (e) => {
    e.preventDefault();
    const code = coupon.trim().toUpperCase();
    const c = COUPONS[code];
    if (!c) {
      toast.error("Invalid code", { description: "Try FRESH10 or ORGANO50." });
      return;
    }
    if (code === "ORGANO50" && cart.subtotal < 500) {
      toast.error("Add a little more", { description: "ORGANO50 needs a basket of ₹500+." });
      return;
    }
    setApplied({ code, ...c });
    setCoupon("");
    toast.success(`${code} applied`, { description: c.label });
  };

  const removeCoupon = () => {
    setApplied(null);
    toast("Coupon removed");
  };

  const discount = (() => {
    if (!applied) return 0;
    if (applied.type === "percent") {
      return Math.round((cart.subtotal * applied.value) / 100);
    }
    return Math.min(applied.value, cart.subtotal);
  })();
  const total = Math.max(0, cart.subtotal - discount);

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-ink/55 backdrop-blur-sm transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-bone shadow-drawer flex flex-col transform transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        aria-label="Shopping basket"
      >
        <header className="flex items-center justify-between p-6 border-b border-ink/10">
          <div>
            <p className="eyebrow">Your basket</p>
            <h3 className="font-display text-3xl text-ink mt-2 tracking-tightest">
              {cart.count > 0
                ? `${cart.count} ${cart.count === 1 ? "item" : "items"}`
                : "Empty"}
            </h3>
          </div>
          <button
            data-testid="cart-close"
            onClick={onClose}
            className="h-10 w-10 grid place-items-center rounded-full hover:bg-ink/5"
            aria-label="Close basket"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {cart.items.length === 0 ? (
            <div className="h-full grid place-items-center text-center">
              <div>
                <div className="mx-auto h-20 w-20 rounded-full bg-sageMist grid place-items-center">
                  <ShoppingBag className="h-7 w-7 text-sageDeep" />
                </div>
                <p className="font-display text-2xl mt-6 text-ink">
                  Your basket awaits.
                </p>
                <p className="text-ink/60 mt-2 text-sm">
                  Add a fresh pack from the shop to get started.
                </p>
                <Link
                  data-testid="empty-cart-shop-cta"
                  to="/shop"
                  onClick={onClose}
                  className="btn-primary mt-7"
                >
                  Browse the shop
                  <span className="knob">
                    <ShoppingBag className="h-4 w-4" />
                  </span>
                </Link>
              </div>
            </div>
          ) : (
            <ul className="space-y-5" data-testid="cart-items-list">
              {cart.items.map((it) => (
                <li
                  key={it.id}
                  data-testid={`cart-item-${it.id}`}
                  className="flex gap-4 bg-boneSoft rounded-2xl p-3"
                >
                  <div className="h-24 w-20 rounded-xl overflow-hidden bg-bone flex-shrink-0">
                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="font-display text-lg text-ink leading-tight tracking-tightest">
                          {it.name}
                        </h4>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-muted mt-1">
                          {it.weight}
                        </p>
                      </div>
                      <button
                        data-testid={`cart-remove-${it.id}`}
                        onClick={() => cart.remove(it.id)}
                        className="h-8 w-8 grid place-items-center rounded-full hover:bg-coral hover:text-white text-muted transition-colors"
                        aria-label={`Remove ${it.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="mt-auto flex items-center justify-between pt-3">
                      <div className="flex items-center border border-ink/15 rounded-full bg-bone">
                        <button
                          data-testid={`cart-dec-${it.id}`}
                          onClick={() => cart.setQty(it.id, it.qty - 1)}
                          className="h-8 w-8 grid place-items-center"
                          aria-label="Decrease"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span data-testid={`cart-qty-${it.id}`} className="w-6 text-center text-sm tabular-nums">
                          {it.qty}
                        </span>
                        <button
                          data-testid={`cart-inc-${it.id}`}
                          onClick={() => cart.setQty(it.id, it.qty + 1)}
                          className="h-8 w-8 grid place-items-center"
                          aria-label="Increase"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <p className="font-display text-xl text-ink tabular-nums">
                        ₹{it.price * it.qty}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {cart.items.length > 0 && (
          <footer className="border-t border-ink/10 p-6 bg-boneSoft space-y-4">
            {/* Coupon */}
            {applied ? (
              <div
                data-testid="cart-applied-coupon"
                className="flex items-center justify-between p-3 rounded-2xl bg-sageMist/60 border border-sage/30"
              >
                <div className="flex items-center gap-2.5 text-sageDeep">
                  <Check className="h-4 w-4" />
                  <div>
                    <p className="text-[13px] font-medium">{applied.code} applied</p>
                    <p className="text-[11px] text-sageDeep/80">{applied.label}</p>
                  </div>
                </div>
                <button
                  data-testid="cart-remove-coupon"
                  onClick={removeCoupon}
                  className="text-xs text-coral hover:underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <form
                data-testid="cart-coupon-form"
                onSubmit={applyCoupon}
                className="flex items-center gap-2 bg-bone rounded-full border border-ink/15 p-1 pl-4"
              >
                <Tag className="h-4 w-4 text-muted flex-shrink-0" />
                <input
                  data-testid="cart-coupon-input"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  placeholder="Coupon code"
                  className="flex-1 bg-transparent outline-none text-sm py-2 tracking-wider uppercase placeholder:text-muted placeholder:normal-case"
                />
                <button
                  data-testid="cart-coupon-apply"
                  type="submit"
                  className="h-9 px-4 rounded-full bg-ink text-bone text-xs font-medium hover:bg-coral transition-colors"
                >
                  Apply
                </button>
              </form>
            )}

            {/* Totals */}
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between text-ink/70">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal" className="tabular-nums">₹{cart.subtotal}</span>
              </div>
              {discount > 0 && (
                <div className="flex items-center justify-between text-sageDeep">
                  <span>Discount ({applied.code})</span>
                  <span data-testid="cart-discount" className="tabular-nums">−₹{discount}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-ink/70">
                <span className="inline-flex items-center gap-1.5">
                  <Truck className="h-3.5 w-3.5" />
                  Delivery (Nagpur)
                </span>
                <span className="text-sageDeep font-medium">Free</span>
              </div>
              <div className="border-t border-ink/10 mt-3 pt-3 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.28em] text-muted">Total</span>
                <span
                  data-testid="cart-total"
                  className="font-display text-3xl tabular-nums tracking-tightest"
                >
                  ₹{total}
                </span>
              </div>
            </div>

            <a
              data-testid="cart-whatsapp-order"
              href={whatsappLink(buildCartMessage(cart.items, total))}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                // Persist a draft order — non-blocking, failures are silent.
                saveOrder({
                  items: cart.items.map((i) => ({
                    id: i.id,
                    name: i.name,
                    weight: i.weight,
                    price: i.price,
                    qty: i.qty,
                  })),
                  subtotal: cart.subtotal,
                  discount,
                  total,
                  coupon: applied ? applied.code : null,
                  channel: "whatsapp",
                }).catch((err) => console.warn("saveOrder failed", err));
              }}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp hover:bg-[#178a45] text-white py-4 text-sm font-medium tracking-wide"
            >
              <WhatsAppIcon className="h-4 w-4" />
              Order on WhatsApp · ₹{total}
            </a>
            <button
              data-testid="cart-checkout"
              onClick={() =>
                toast("Please order on WhatsApp", {
                  description: "All orders are placed and confirmed via WhatsApp for now.",
                })
              }
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-ink/20 hover:border-coral hover:text-coral text-ink py-3 text-sm font-medium tracking-wide"
            >
              <Lock className="h-4 w-4" />
              Online payment coming soon
            </button>
            <button
              data-testid="cart-clear"
              onClick={cart.clear}
              className="w-full text-xs text-muted hover:text-coral py-1 transition-colors"
            >
              Empty basket
            </button>
          </footer>
        )}
      </aside>
    </>
  );
}
